import { gql } from '@apollo/client';

const CHECKLIST_FIELDS = `
    businessId
    businessName
    status
    intro
    progress {
        total
        done
    }
    sections {
        key
        title
        badge
        phase
        prerequisites
        items {
            id
            label
            note
            optional
            phase
            done
        }
    }
    comments {
        _id
        itemId
        authorType
        authorName
        text
        parentId
        createdAt
    }
`;

/** PUBLIC (no auth) — the checklist token is the authorization. */
export const SET_CHECKLIST_ITEM_DONE_MUTATION = gql`
    mutation SetChecklistItemDone($body: SetChecklistItemDoneInput!) {
        setChecklistItemDone(body: $body) {
            ${CHECKLIST_FIELDS}
        }
    }
`;

export const ADD_CHECKLIST_COMMENT_MUTATION = gql`
    mutation AddChecklistComment($body: AddChecklistCommentInput!) {
        addChecklistComment(body: $body) {
            ${CHECKLIST_FIELDS}
        }
    }
`;

const MANUAL_FIELDS = `
    _id
    status
    templateVariant
    templateVersion
    intro
    checklistToken
    checklistUrl
    checklistShortUrl
    sentAt
    sections {
        key
        title
        badge
        phase
        prerequisites
        items {
            id
            label
            note
            optional
            phase
            done
        }
    }
`;

/** PUBLIC (no auth) — the form token is the authorization. */
export const SUBMIT_ONBOARDING_FORM_MUTATION = gql`
    mutation SubmitOnboardingForm($body: SubmitOnboardingFormInput!) {
        submitOnboardingForm(body: $body) {
            businessId
            status
        }
    }
`;

export const SEND_ONBOARDING_FORM_MUTATION = gql`
    mutation SendOnboardingForm($businessId: String!, $sendEmail: Boolean) {
        sendOnboardingForm(businessId: $businessId, sendEmail: $sendEmail) {
            _id
            status
            formToken
            formUrl
            formShortUrl
        }
    }
`;

export const REGENERATE_ONBOARDING_MANUAL_MUTATION = gql`
    mutation RegenerateOnboardingManual($submissionId: String!) {
        regenerateOnboardingManual(submissionId: $submissionId) {
            ${MANUAL_FIELDS}
        }
    }
`;

export const UPDATE_ONBOARDING_MANUAL_MUTATION = gql`
    mutation UpdateOnboardingManual($body: UpdateOnboardingManualInput!) {
        updateOnboardingManual(body: $body) {
            ${MANUAL_FIELDS}
        }
    }
`;

export const SEND_ONBOARDING_MANUAL_MUTATION = gql`
    mutation SendOnboardingManual($manualId: String!, $sendEmail: Boolean) {
        sendOnboardingManual(manualId: $manualId, sendEmail: $sendEmail) {
            ${MANUAL_FIELDS}
        }
    }
`;

const TEMPLATE_FIELDS = `
    variant
    intro
    customised
    published
    revision
    publishedAt
    seedVersion
    sections {
        key
        title
        badge
        phase
        prerequisites
        items {
            id
            label
            note
            optional
            requiredIfService
            optionalFeatureKey
        }
    }
`;

const QUESTIONNAIRE_FIELDS = `
    definition
    customised
    published
    revision
    publishedAt
    seedVersion
`;

/** Staff (@Auth) — CMS-editable onboarding questionnaire (singleton). */
export const UPDATE_ONBOARDING_QUESTIONNAIRE_MUTATION = gql`
    mutation UpdateOnboardingQuestionnaire($definition: JSON!) {
        updateOnboardingQuestionnaire(definition: $definition) {
            ${QUESTIONNAIRE_FIELDS}
        }
    }
`;

export const PUBLISH_ONBOARDING_QUESTIONNAIRE_MUTATION = gql`
    mutation PublishOnboardingQuestionnaire {
        publishOnboardingQuestionnaire {
            ${QUESTIONNAIRE_FIELDS}
        }
    }
`;

/** Staff (@Auth) — save the draft; publishing is a separate explicit step. */
export const UPDATE_ONBOARDING_TEMPLATE_MUTATION = gql`
    mutation UpdateOnboardingTemplate($body: UpdateOnboardingTemplateInput!) {
        updateOnboardingTemplate(body: $body) {
            ${TEMPLATE_FIELDS}
        }
    }
`;

/** Copies the draft to the published snapshot — generation switches to it. */
export const PUBLISH_ONBOARDING_TEMPLATE_MUTATION = gql`
    mutation PublishOnboardingTemplate($variant: String!) {
        publishOnboardingTemplate(variant: $variant) {
            ${TEMPLATE_FIELDS}
        }
    }
`;

/** Staff (@Auth) — reply on the two-way checklist thread from the CMS. */
export const STAFF_ADD_CHECKLIST_COMMENT_MUTATION = gql`
    mutation StaffAddChecklistComment($body: StaffAddChecklistCommentInput!) {
        staffAddChecklistComment(body: $body) {
            ${CHECKLIST_FIELDS}
        }
    }
`;

export const DELETE_CHECKLIST_COMMENT_MUTATION = gql`
    mutation DeleteChecklistComment($commentId: String!) {
        deleteChecklistComment(commentId: $commentId) {
            message
        }
    }
`;

/** Revokes the old link (it 404s immediately) and returns a fresh one. */
export const REISSUE_ONBOARDING_FORM_LINK_MUTATION = gql`
    mutation ReissueOnboardingFormLink($businessId: String!) {
        reissueOnboardingFormLink(businessId: $businessId) {
            _id
            status
            formToken
            formUrl
        }
    }
`;

export const REISSUE_ONBOARDING_CHECKLIST_LINK_MUTATION = gql`
    mutation ReissueOnboardingChecklistLink($manualId: String!) {
        reissueOnboardingChecklistLink(manualId: $manualId) {
            ${MANUAL_FIELDS}
        }
    }
`;
